import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, FolderKanban, Bell, Check, Archive, ArchiveRestore, Lock, Trash2, MessageSquarePlus, Plus, Hash } from 'lucide-react';
import { projectsApi, pluginsApi, organizationsApi, SlackChannel } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';
import { useAuthStore, getIsAgencyAdmin } from '@/stores/auth.store';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { assignableClientOrgs } from '@/lib/client-orgs';

interface ProjectMember {
  userId: string;
  role: string;
  user: {
    id: string;
    name: string;
    email: string;
    avatarUrl?: string;
  };
}

interface EditProjectModalProps {
  isOpen: boolean;
  project: { id: string; name: string; description?: string; archivedAt?: string | null } | null;
  onClose: () => void;
  onSuccess?: () => void;
}

type TabType = 'details' | 'notifications' | 'discussionTypes' | 'slack';

interface DiscussionTypeConfig {
  slug: string;
  label: string;
  labelPlural: string;
  position: number;
}

function pluralize(word: string): string {
  if (!word) return word;
  const lower = word.toLowerCase();
  if (lower.endsWith('s') || lower.endsWith('sh') || lower.endsWith('ch') || lower.endsWith('x') || lower.endsWith('z')) return word + 'es';
  if (lower.endsWith('y') && !/[aeiou]y$/i.test(lower)) return word.slice(0, -1) + 'ies';
  return word + 's';
}

const RESERVED_SLUGS = ['discussions', 'tasks', 'files', 'events', 'board', 'custom'];

function generateSlug(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function EditProjectModal({ isOpen, project, onClose, onSuccess }: EditProjectModalProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [initialIsPrivate, setInitialIsPrivate] = useState(false);
  const [clientsCanCreate, setClientsCanCreate] = useState(true);
  const [initialClientsCanCreate, setInitialClientsCanCreate] = useState(true);
  const [clientOrgId, setClientOrgId] = useState('');
  const [initialClientOrgId, setInitialClientOrgId] = useState('');
  const [selectedDefaultIds, setSelectedDefaultIds] = useState<string[]>([]);
  const [initialDefaultIds, setInitialDefaultIds] = useState<string[]>([]);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [discussionTypes, setDiscussionTypes] = useState<DiscussionTypeConfig[]>([]);
  // What this project calls discussions — 'Requests' for a support client,
  // default for everyone else. Both empty = the stock name.
  const [labelSingular, setLabelSingular] = useState('');
  const [labelPlural, setLabelPlural] = useState('');
  const [initialLabelSingular, setInitialLabelSingular] = useState('');
  const [initialLabelPlural, setInitialLabelPlural] = useState('');
  const [initialDiscussionTypes, setInitialDiscussionTypes] = useState<DiscussionTypeConfig[]>([]);
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [typeError, setTypeError] = useState('');

  // Slack channel override state
  const [slackChannelId, setSlackChannelId] = useState<string>('');
  const [slackChannelName, setSlackChannelName] = useState<string>('');
  const [initialSlackChannelId, setInitialSlackChannelId] = useState<string>('');

  // Admin of THIS project: Solid admins anywhere, a partner agency's on its own.
  // Slack stays Solid-only (so do its API routes).
  const { isProjectAdmin: isAgencyAdmin } = useProjectRoles(project?.id);
  const isSolidAdmin = getIsAgencyAdmin(user);

  // Fetch the full project data to get the members list
  const { data: projectData } = useQuery({
    queryKey: ['project', project?.id],
    queryFn: () => projectsApi.get(project!.id),
    enabled: isOpen && !!project?.id,
  });

  // Get project members from the fetched project data
  const projectMembers: ProjectMember[] = projectData?.members || [];

  // The agency that runs this project: Solid, or the partner agency that owns it.
  // Only its owners can toggle private.
  const agencyOrg =
    user?.organizations?.find((org: any) => org.type === 'AGENCY') ??
    user?.organizations?.find(
      (org: any) => org.type === 'PARTNER' && org.id === (projectData as any)?.organizationId,
    );
  const isAgencyOwner = agencyOrg?.role === 'org_owner';

  // The organization that OWNS the project. When that is a client org rather
  // than the agency, "Internal project (no client)" is false on its face: the
  // project list shows that org's name and logo, and its members can see the
  // project through ownership — no client assignment involved. The two screens
  // must not disagree, so this field has to name the owner in that case.
  const owningOrg = (projectData as any)?.organization ?? null;
  const ownedByClientOrg = !!(
    (projectData as any)?.organizationId &&
    agencyOrg?.id &&
    (projectData as any).organizationId !== agencyOrg.id
  );

  // Get current notification defaults
  const { data: currentDefaults = [] } = useQuery({
    queryKey: ['project-notification-defaults', project?.id],
    queryFn: () => projectsApi.getNotificationDefaults(project!.id),
    enabled: isOpen && !!project?.id,
  });

  // Organizations available as the project's client — agency admins only
  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => organizationsApi.list(),
    enabled: isOpen && isAgencyAdmin,
  });

  // Slack: Get existing project override
  const { data: slackOverrideData } = useQuery({
    queryKey: ['slack-project-override', project?.id],
    queryFn: () => pluginsApi.slack.getProjectOverrides(project!.id),
    enabled: isOpen && !!project?.id && isSolidAdmin,
    retry: false,
  });

  // Slack: Get available channels (needs the installation ID from the override response)
  const { data: slackChannelsData } = useQuery({
    queryKey: ['slack-channels', slackOverrideData?.installationId],
    queryFn: () => pluginsApi.slack.listChannels(slackOverrideData!.installationId),
    enabled: !!slackOverrideData?.installationId,
  });

  const slackChannels: SlackChannel[] = slackChannelsData?.channels || [];
  const hasSlackInstallation = !!slackOverrideData?.installationId;

  // Initialize Slack override from fetched data
  useEffect(() => {
    if (slackOverrideData?.mappings?.length) {
      const first = slackOverrideData.mappings[0];
      const config = first.channelConfig as { channelId?: string; channelName?: string };
      setSlackChannelId(config.channelId || '');
      setSlackChannelName(config.channelName || '');
      setInitialSlackChannelId(config.channelId || '');
    } else {
      setSlackChannelId('');
      setSlackChannelName('');
      setInitialSlackChannelId('');
    }
  }, [slackOverrideData]);

  // Initialize form when project or defaults change
  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description || '');
    }
  }, [project]);

  // Initialize isPrivate and discussion types from fetched project data
  useEffect(() => {
    if (projectData) {
      setIsPrivate(!!projectData.isPrivate);
      setInitialIsPrivate(!!projectData.isPrivate);
      const settings = (projectData.settings as any) || {};
      const types: DiscussionTypeConfig[] = settings.discussionTypes || [];
      setDiscussionTypes(types);
      setInitialDiscussionTypes(types);
      const cCreate = settings.clientsCanCreateTasks !== false;
      setClientsCanCreate(cCreate);
      setInitialClientsCanCreate(cCreate);
      const rawLabel = (settings.discussionsLabel as { singular?: string; plural?: string }) || {};
      setLabelSingular(rawLabel.singular || '');
      setLabelPlural(rawLabel.plural || '');
      setInitialLabelSingular(rawLabel.singular || '');
      setInitialLabelPlural(rawLabel.plural || '');
      const cOrg = settings.clientOrganizationId || '';
      setClientOrgId(cOrg);
      setInitialClientOrgId(cOrg);
    }
  }, [projectData]);

  // Initialize notification defaults when data is loaded
  // Use JSON.stringify to create a stable dependency
  const currentDefaultsStr = JSON.stringify(currentDefaults);
  useEffect(() => {
    const defaults = JSON.parse(currentDefaultsStr) as string[];
    setSelectedDefaultIds(defaults);
    setInitialDefaultIds(defaults);
  }, [currentDefaultsStr]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('details');
      setDeleteConfirmText('');
    }
  }, [isOpen]);

  // Check if there are unsaved changes
  const hasDetailsChanges = project && (name !== project.name || description !== (project.description || ''));
  const hasPrivacyChanges = isPrivate !== initialIsPrivate;
  const hasNotificationChanges = JSON.stringify(selectedDefaultIds.sort()) !== JSON.stringify(initialDefaultIds.sort());
  const hasDiscussionTypeChanges = JSON.stringify(discussionTypes) !== JSON.stringify(initialDiscussionTypes);
  const hasLabelChanges =
    labelSingular.trim() !== initialLabelSingular.trim() ||
    labelPlural.trim() !== initialLabelPlural.trim();
  const hasTaskPermChanges = clientsCanCreate !== initialClientsCanCreate;
  const hasClientOrgChanges = clientOrgId !== initialClientOrgId;
  const hasSlackChanges = slackChannelId !== initialSlackChannelId;
  const hasChanges = hasDetailsChanges || hasPrivacyChanges || hasNotificationChanges || hasDiscussionTypeChanges || hasTaskPermChanges || hasClientOrgChanges || hasSlackChanges || hasLabelChanges;

  const updateDetailsMutation = useMutation({
    mutationFn: (data: { name: string; description: string; isPrivate?: boolean; settings?: Record<string, any> }) =>
      projectsApi.update(project!.id, data),
  });

  const updateNotificationsMutation = useMutation({
    mutationFn: (userIds: string[]) =>
      projectsApi.setNotificationDefaults(project!.id, userIds),
  });

  const archiveMutation = useMutation({
    mutationFn: () => projectsApi.archive(project!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', project!.id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => projectsApi.unarchive(project!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', project!.id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => projectsApi.delete(project!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onClose();
      navigate('/projects');
    },
  });

  const isArchived = !!project?.archivedAt;
  const isUnknownClientOrg = !!clientOrgId && organizations.length > 0
    && !organizations.some((org) => org.id === clientOrgId);

  const handleAddType = () => {
    const label = newTypeLabel.trim();
    if (!label) return;

    const slug = generateSlug(label);

    if (!slug) {
      setTypeError('Label must contain at least one letter or number.');
      return;
    }

    if (RESERVED_SLUGS.includes(slug)) {
      setTypeError(`"${slug}" is a reserved name. Choose a different label.`);
      return;
    }

    if (discussionTypes.some((dt) => dt.slug === slug)) {
      setTypeError(`A type with slug "${slug}" already exists.`);
      return;
    }

    const nextPosition = discussionTypes.length > 0
      ? Math.max(...discussionTypes.map((dt) => dt.position)) + 1
      : 0;

    setDiscussionTypes((prev) => [...prev, { slug, label, labelPlural: pluralize(label), position: nextPosition }]);
    setNewTypeLabel('');
    setTypeError('');
  };

  const handleSave = async () => {
    if (!project) return;

    try {
      // Save details and/or discussion types if changed
      if (hasDetailsChanges || hasPrivacyChanges || hasDiscussionTypeChanges || hasTaskPermChanges || hasClientOrgChanges || hasLabelChanges) {
        await updateDetailsMutation.mutateAsync({
          name: name.trim(),
          description: description.trim(),
          ...(hasPrivacyChanges && { isPrivate }),
          ...((hasDiscussionTypeChanges || hasTaskPermChanges || hasClientOrgChanges || hasLabelChanges) && {
            settings: {
              ...(hasDiscussionTypeChanges && { discussionTypes }),
              // null clears the override; the stock name comes back.
              ...(hasLabelChanges && {
                discussionsLabel:
                  labelSingular.trim() || labelPlural.trim()
                    ? { singular: labelSingular.trim() || null, plural: labelPlural.trim() || null }
                    : null,
              }),
              ...(hasTaskPermChanges && {
                clientsCanCreateTasks: clientsCanCreate,
              }),
              // null clears the key server-side, making the project internal again
              ...(hasClientOrgChanges && { clientOrganizationId: clientOrgId || null }),
            },
          }),
        });
      }

      // Save notification defaults if changed
      if (hasNotificationChanges) {
        await updateNotificationsMutation.mutateAsync(selectedDefaultIds);
      }

      // Save Slack channel override if changed
      if (hasSlackChanges) {
        if (slackChannelId) {
          await pluginsApi.slack.setProjectOverride(project.id, slackChannelId, slackChannelName);
        } else {
          await pluginsApi.slack.deleteProjectOverride(project.id);
        }
      }

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      queryClient.invalidateQueries({ queryKey: ['project-notification-defaults', project.id] });
      if (hasClientOrgChanges) {
        queryClient.invalidateQueries({ queryKey: ['organizations'] });
      }
      if (hasSlackChanges) {
        queryClient.invalidateQueries({ queryKey: ['slack-project-override', project.id] });
      }

      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to save project settings:', error);
    }
  };

  const toggleDefaultUser = (userId: string) => {
    setSelectedDefaultIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const isSaving = updateDetailsMutation.isPending || updateNotificationsMutation.isPending;

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-elevated animate-scale-in overflow-hidden max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-accent/10">
              <FolderKanban className="w-5 h-5 text-accent" />
            </div>
            <h2 className="text-heading-4 text-secondary">Project Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-grey-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-grey-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-grey-100">
          <button
            onClick={() => setActiveTab('details')}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
              activeTab === 'details'
                ? "border-accent text-accent"
                : "border-transparent text-grey-500 hover:text-secondary"
            )}
          >
            <FolderKanban className="w-4 h-4" />
            Details
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
              activeTab === 'notifications'
                ? "border-accent text-accent"
                : "border-transparent text-grey-500 hover:text-secondary"
            )}
          >
            <Bell className="w-4 h-4" />
            Notifications
            {hasNotificationChanges && (
              <span className="w-2 h-2 rounded-full bg-accent" />
            )}
          </button>
          {isAgencyAdmin && (
            <button
              onClick={() => setActiveTab('discussionTypes')}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                activeTab === 'discussionTypes'
                  ? "border-accent text-accent"
                  : "border-transparent text-grey-500 hover:text-secondary"
              )}
            >
              <MessageSquarePlus className="w-4 h-4" />
              Types
              {(hasDiscussionTypeChanges || hasLabelChanges) && (
                <span className="w-2 h-2 rounded-full bg-accent" />
              )}
            </button>
          )}
          {isSolidAdmin && hasSlackInstallation && (
            <button
              onClick={() => setActiveTab('slack')}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                activeTab === 'slack'
                  ? "border-accent text-accent"
                  : "border-transparent text-grey-500 hover:text-secondary"
              )}
            >
              <Hash className="w-4 h-4" />
              Slack
              {hasSlackChanges && (
                <span className="w-2 h-2 rounded-full bg-accent" />
              )}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'details' && (
            /* Details Tab */
            <div className="px-6 py-6 space-y-4">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-secondary mb-2">
                  Project Name <span className="text-error">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Website Redesign"
                  className="input"
                  required
                  autoFocus
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-secondary mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the project..."
                  className="input min-h-[100px] resize-none"
                  rows={3}
                />
              </div>

              {/* Client organization - agency admins only. This drives which client
                  sees the project, and which org it groups under in announcements. */}
              {isAgencyAdmin && (
                <div>
                  <label htmlFor="clientOrg" className="block text-sm font-medium text-secondary mb-2">
                    Client Organization
                  </label>
                  <select
                    id="clientOrg"
                    value={clientOrgId}
                    onChange={(e) => setClientOrgId(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-grey-200 rounded-lg text-sm text-secondary focus:ring-2 focus:ring-accent/50 focus:border-accent"
                  >
                    <option value="">
                      {ownedByClientOrg
                        ? `No client assigned — owned by ${owningOrg?.name ?? 'its organization'}`
                        : 'Internal project (no client)'}
                    </option>
                    {assignableClientOrgs(organizations, agencyOrg).map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                    {isUnknownClientOrg && (
                      <option value={clientOrgId}>Unknown org ({clientOrgId})</option>
                    )}
                  </select>
                  <p className={cn('text-xs mt-1.5', isUnknownClientOrg ? 'text-amber-600' : 'text-grey-500')}>
                    {isUnknownClientOrg
                      ? 'This project points at an organization that no longer exists or has lost Hub access. Pick a valid one to fix how it appears in announcements and org views.'
                      : ownedByClientOrg && !clientOrgId
                        ? `This project belongs to ${owningOrg?.name ?? 'a client organization'}, so its members can already see it. Assign an organization here only to share it with a second one.`
                        : 'Members of this organization can see the project. Leave as internal for agency-only work.'}
                  </p>
                </div>
              )}

              {/* Private Project Toggle - only visible to org owners */}
              {isAgencyOwner && (
                <div className="pt-4 mt-4 border-t border-grey-200">
                  <label className="flex items-center gap-3 p-4 rounded-xl bg-grey-50 border border-grey-200 cursor-pointer hover:bg-grey-100 transition-colors">
                    <div className={`w-10 h-6 rounded-full relative transition-colors ${isPrivate ? 'bg-primary' : 'bg-grey-300'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${isPrivate ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                    </div>
                    <input
                      type="checkbox"
                      checked={isPrivate}
                      onChange={(e) => setIsPrivate(e.target.checked)}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-grey-600" />
                        <span className="text-sm font-medium text-secondary">Private Project</span>
                      </div>
                      <p className="text-xs text-grey-500 mt-0.5">Only visible to members. Even admins cannot see this project unless they are members.</p>
                    </div>
                  </label>
                </div>
              )}

              {/* Client task permissions - agency admins only */}
              {isAgencyAdmin && (
                <div className="pt-4 mt-4 border-t border-grey-200 space-y-3">
                  <h4 className="text-sm font-medium text-secondary">Client task permissions</h4>
                  <label className="flex items-center gap-3 p-4 rounded-xl bg-grey-50 border border-grey-200 cursor-pointer hover:bg-grey-100 transition-colors">
                    <div className={`w-10 h-6 rounded-full relative transition-colors ${clientsCanCreate ? 'bg-primary' : 'bg-grey-300'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${clientsCanCreate ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                    </div>
                    <input type="checkbox" checked={clientsCanCreate} onChange={(e) => setClientsCanCreate(e.target.checked)} className="sr-only" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-secondary">Clients can create tasks</span>
                      <p className="text-xs text-grey-500 mt-0.5">Allow client members to add new tasks in this project. Clients can edit the title/description of tasks they created until the task is assigned; status, priority, assignee and dates are always agency-only.</p>
                    </div>
                  </label>
                </div>
              )}

              {/* Archive Section */}
              <div className="pt-4 mt-4 border-t border-grey-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-secondary">
                      {isArchived ? 'Unarchive Project' : 'Archive Project'}
                    </h4>
                    <p className="text-xs text-grey-500 mt-0.5">
                      {isArchived
                        ? 'Restore this project to make it active again.'
                        : 'Archived projects are hidden from the main list.'}
                    </p>
                  </div>
                  <Button
                    variant={isArchived ? 'outline' : 'ghost'}
                    size="sm"
                    onClick={() => isArchived ? unarchiveMutation.mutate() : archiveMutation.mutate()}
                    disabled={archiveMutation.isPending || unarchiveMutation.isPending}
                    className={!isArchived ? 'text-grey-600 hover:text-secondary' : ''}
                  >
                    {archiveMutation.isPending || unarchiveMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isArchived ? (
                      <>
                        <ArchiveRestore className="w-4 h-4" />
                        Unarchive
                      </>
                    ) : (
                      <>
                        <Archive className="w-4 h-4" />
                        Archive
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Delete Section - admin only */}
              {isAgencyAdmin && (
                <div className="pt-4 mt-4 border-t border-error/20">
                  <h4 className="text-sm font-medium text-error mb-1">Delete Project</h4>
                  <p className="text-xs text-grey-500 mb-3">
                    Permanently delete this project and all associated data (discussions, tasks, files, events). This action cannot be undone.
                  </p>
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder='Type "delete" to confirm'
                      className="input flex-1 text-sm"
                      disabled={deleteMutation.isPending}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteMutation.mutate()}
                      disabled={deleteConfirmText.toLowerCase() !== 'delete' || deleteMutation.isPending}
                      className="text-error hover:bg-error/10 hover:text-error shrink-0"
                    >
                      {deleteMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'notifications' && (
            <div className="px-6 py-4">
              <p className="text-sm text-grey-500 mb-4">
                Select members who will be notified by default when new discussions are posted.
              </p>

              {projectMembers.length === 0 ? (
                <div className="py-8 text-center text-grey-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No members available</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {projectMembers.map((member) => {
                    const isSelected = selectedDefaultIds.includes(member.userId);
                    return (
                      <label
                        key={member.userId}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors",
                          isSelected ? "bg-accent/5" : "bg-grey-50 hover:bg-grey-100"
                        )}
                      >
                        <div
                          className={cn(
                            "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
                            isSelected
                              ? "bg-accent border-accent"
                              : "border-grey-300"
                          )}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleDefaultUser(member.userId)}
                          className="sr-only"
                        />
                        <Avatar
                          src={member.user.avatarUrl}
                          name={member.user.name}
                          userId={member.userId}
                          size="md"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-secondary truncate">{member.user.name}</p>
                            {member.role === 'admin' && (
                              <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full">
                                Admin
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-grey-500 truncate">{member.user.email}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {activeTab === 'discussionTypes' && (
            <div className="px-6 py-4">
              {/* The concept itself can wear the client's word — 'Requests',
                  'Tickets' — everywhere in THIS project: tab, headings, the
                  New button, breadcrumbs. Routes and data never change. */}
              <div className="mb-5 p-3 rounded-xl bg-grey-50 border border-grey-200">
                <p className="text-sm font-medium text-secondary mb-1">What this project calls discussions</p>
                <p className="text-xs text-grey-500 mb-3">
                  Leave blank for &ldquo;Discussions&rdquo;. Some clients think in requests — name it what they call it.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="disc-label-singular" className="block text-xs font-medium text-grey-500 mb-1">Singular</label>
                    <input
                      id="disc-label-singular"
                      type="text"
                      value={labelSingular}
                      onChange={(e) => setLabelSingular(e.target.value)}
                      placeholder="Discussion"
                      className="input"
                    />
                  </div>
                  <div>
                    <label htmlFor="disc-label-plural" className="block text-xs font-medium text-grey-500 mb-1">Plural</label>
                    <input
                      id="disc-label-plural"
                      type="text"
                      value={labelPlural}
                      onChange={(e) => setLabelPlural(e.target.value)}
                      placeholder={labelSingular.trim() ? `${labelSingular.trim()}s` : 'Discussions'}
                      className="input"
                    />
                  </div>
                </div>
              </div>
              <p className="text-sm text-grey-500 mb-4">
                Add custom discussion types to organize conversations. Each type gets its own tab in the project navigation.
              </p>

              {/* Existing types */}
              {discussionTypes.length > 0 && (
                <div className="space-y-2 mb-4">
                  {discussionTypes.map((dt) => (
                    <div
                      key={dt.slug}
                      className="p-3 rounded-xl bg-grey-50 border border-grey-200"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <MessageSquarePlus className="w-4 h-4 text-accent" />
                          <span className="text-xs text-grey-400">/{dt.slug}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setDiscussionTypes((prev) => prev.filter((t) => t.slug !== dt.slug));
                          }}
                          className="p-1.5 rounded-lg hover:bg-grey-200 text-grey-400 hover:text-error transition-colors"
                          title="Remove type"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs text-grey-500 mb-0.5">Singular</label>
                          <input
                            type="text"
                            value={dt.label}
                            onChange={(e) => {
                              const newLabel = e.target.value;
                              setDiscussionTypes((prev) =>
                                prev.map((t) => t.slug === dt.slug ? { ...t, label: newLabel } : t),
                              );
                            }}
                            className="input text-sm py-1.5"
                            placeholder="e.g., Report"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-grey-500 mb-0.5">Plural (tab name)</label>
                          <input
                            type="text"
                            value={dt.labelPlural}
                            onChange={(e) => {
                              const newPlural = e.target.value;
                              setDiscussionTypes((prev) =>
                                prev.map((t) => t.slug === dt.slug ? { ...t, labelPlural: newPlural } : t),
                              );
                            }}
                            className="input text-sm py-1.5"
                            placeholder="e.g., Reports"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new type */}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label htmlFor="newTypeLabel" className="block text-sm font-medium text-secondary mb-1">
                    Add Type <span className="font-normal text-grey-400">(singular)</span>
                  </label>
                  <input
                    id="newTypeLabel"
                    type="text"
                    value={newTypeLabel}
                    onChange={(e) => {
                      setNewTypeLabel(e.target.value);
                      setTypeError('');
                    }}
                    placeholder="e.g., Report, Design, Update"
                    className="input text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddType();
                      }
                    }}
                  />
                  {typeError && (
                    <p className="text-xs text-error mt-1">{typeError}</p>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddType}
                  disabled={!newTypeLabel.trim()}
                >
                  <Plus className="w-4 h-4" />
                  Add
                </Button>
              </div>

              {discussionTypes.length === 0 && (
                <div className="py-6 text-center text-grey-400 mt-4">
                  <MessageSquarePlus className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No custom types yet</p>
                </div>
              )}
            </div>
          )}
          {activeTab === 'slack' && (
            <div className="px-6 py-4">
              <p className="text-sm text-grey-500 mb-4">
                Override the default Slack channel for this project's notifications. When set, all Hub events for this project will be sent to the selected channel instead of the organization or default channel.
              </p>

              <div>
                <label htmlFor="slackChannel" className="block text-sm font-medium text-secondary mb-2">
                  Slack Channel
                </label>
                <select
                  id="slackChannel"
                  value={slackChannelId}
                  onChange={(e) => {
                    const ch = slackChannels.find((c) => c.id === e.target.value);
                    setSlackChannelId(e.target.value);
                    setSlackChannelName(ch?.name || '');
                  }}
                  className="w-full px-3 py-2 bg-white border border-grey-200 rounded-lg text-sm text-secondary focus:ring-2 focus:ring-accent/50 focus:border-accent"
                >
                  <option value="">Use organization/default channel</option>
                  {slackChannels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.is_private ? '🔒' : '#'} {channel.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-grey-500 mt-2">
                  {slackChannelId
                    ? `Notifications for this project will go to #${slackChannelName}`
                    : 'Notifications will use the organization-level or default channel'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Single Save Button */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-grey-50 border-t border-grey-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || !name.trim() || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
